import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AngularReactUrlMapperService } from 'src/app/util/angular-react-url-mapper.service';
import { IframeNavigatorService } from 'src/app/util/iframe-navigator.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-react-iframe',
  templateUrl: './react-iframe.component.html',
  styleUrls: ['./react-iframe.component.scss'],
})

/**
 * Note this component will cause an https certificate/security warning in Google Chrome
 * when loading from localhost because of the generated different certs/proxy config
 * in this app vs the react app. To avoid this, you can enable a flag in google chrome.
 *
 * chrome://flags/#allow-insecure-localhost
 */
export class ReactIframeComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('iframe') iframeElement: ElementRef<HTMLIFrameElement>;
  iframeUrl: SafeUrl;
  createRoute: string | null = null;
  createPrivilege: string | null = null;
  nomenclature: string | null = null;
  createMenu = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private domSanitizer: DomSanitizer, 
    private iframeNavigator: IframeNavigatorService,
    private urlMapper: AngularReactUrlMapperService
  ) {}

  ngOnInit() {
    // Only apply URL mapping for list pages that need it
    const currentUrl = this.router.url;
    const needsMapping = this.urlMapper.shouldUseReact(currentUrl.split('?')[0]);
    let reactUrl = needsMapping ? this.urlMapper.angularToReact(currentUrl) : currentUrl;
    
    // Add default parameters based on entity type if no query params exist
    const entityType = this.route.snapshot.data['entityType'];
    
    // Set up header action button based on entity type
    this.setHeaderActionButton(entityType);
    
    // Read from route data, defaulting to true if not provided
    const routeData = this.route.snapshot.data;
    const isLegacy = routeData['isLegacy'] !== undefined ? routeData['isLegacy'] : true;

    const url = `${environment.dashboard_url}${isLegacy ? 'legacy' : ''}${reactUrl}`;
    this.iframeUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(url);
  }

  private setHeaderActionButton(entityType: string): void {
    if (this.route.snapshot.data['headerCreateAction'] === 'jobsite') {
      this.createRoute = '/map/locations/create';
      this.createPrivilege = 'remote_location/create';
      this.nomenclature = 'remote_location';
      return;
    }

    const buttonConfig: { [key: string]: { route: string; privilege: string; nomenclature: string } } = {
      'jobsites': {
        route: '/map/locations/create',
        privilege: 'remote_location/create',
        nomenclature: 'remote_location'
      },
      'warehouses': {
        route: '/map/warehouses/create',
        privilege: 'fixed_location/create',
        nomenclature: 'fixed_location'
      },
      'vehicles': {
        route: '/map/vehicles/create',
        privilege: 'mobile_location/create',
        nomenclature: 'mobile_location'
      },
      'trailers': {
        route: '/map/trailers/create',
        privilege: 'roving_location/create',
        nomenclature: 'roving_location'
      }
    };

    const config = buttonConfig[entityType];
    if (config) {
      this.createRoute = config.route;
      this.createPrivilege = config.privilege;
      this.nomenclature = config.nomenclature;
      this.createMenu = entityType !== 'jobsites';
      console.log('ReactIframeComponent: Setting header button', {
        entityType,
        route: this.createRoute,
        privilege: this.createPrivilege,
        nomenclature: this.nomenclature
      });
    } else {
      console.log('ReactIframeComponent: No button config found for entityType:', entityType);
    }
  }

  ngAfterViewInit() {
    this.iframeNavigator.setIframe(this.iframeElement.nativeElement);
  }

  ngOnDestroy() {
    this.iframeNavigator.setIframe(null);
  }
}
